import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const services = [
  { name: 'Flight Booking',   slug: 'flight-booking' },
  { name: 'Visa Assistance',  slug: 'visa-assistance' },
  { name: 'Hotel Booking',    slug: 'hotel-booking' },
  { name: 'Travel Insurance', slug: 'travel-insurance' },
  { name: 'Airport Transfer', slug: 'airport-transfer' },
  { name: 'Cruise Packages',  slug: 'cruise-packages' },
  { name: 'Holiday Packages', slug: 'holiday-packages' },
  { name: 'Corporate Travel', slug: 'corporate-travel-services' }
];

function NavDropdown({ label, children }) {
  const [open, setOpen] = useState(false);
  const closeTimer      = useRef(null);

  function handleMouseEnter() { clearTimeout(closeTimer.current); setOpen(true); }
  function handleMouseLeave() { closeTimer.current = setTimeout(function() { setOpen(false); }, 150); }
  function handleClose() { setOpen(false); }

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span style={{
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 4,
        fontWeight: 600, fontSize: '0.92rem',
        color: open ? 'var(--bj-clay)' : 'inherit',
        userSelect: 'none'
      }}>
        {label}
        <span style={{
          fontSize: '0.65rem', opacity: 0.6,
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s', display: 'inline-block'
        }}>▼</span>
      </span>

      {open && (
        <div style={{
          position: 'absolute', top: '100%',
          left: '-20px', right: '-20px',
          height: 16, zIndex: 99
        }} />
      )}

<div style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: '50%',
        transform: open ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-8px)',
        background: '#fff',
        boxShadow: '0 12px 40px rgba(17,45,78,0.14)',
        borderRadius: 12,
        padding: '8px 0',
        zIndex: 100,
        minWidth: 220,
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'all' : 'none',
        transition: 'opacity 0.2s ease, transform 0.2s ease'
      }}
        onClick={handleClose}
      >
        {children}
      </div>
    </div>
  );
}

function DropdownLink({ to, icon, label, sub, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 18px', textDecoration: 'none',
        background: hovered ? 'var(--bj-cream-soft)' : 'transparent',
        transition: 'background 0.15s'
      }}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
    >
      {icon && <span style={{ fontSize: '1rem', flexShrink: 0 }}>{icon}</span>}
      <div>
        <p style={{
          margin: 0, fontWeight: 600, fontSize: '0.9rem',
          color: hovered ? 'var(--bj-clay)' : 'var(--bj-espresso)',
          transition: 'color 0.15s'
        }}>{label}</p>
        {sub && <p style={{ margin: 0, fontSize: '0.73rem', opacity: 0.5, marginTop: 1 }}>{sub}</p>}
      </div>
    </Link>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--bj-sand)', margin: '4px 12px' }} />;
}

// Mobile accordion item
function MobileAccordion({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--bj-sand)' }}>
      <button
        onClick={function() { setOpen(!open); }}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', padding: '14px 0',
          background: 'none', border: 'none',
          fontFamily: 'var(--font-body)', fontWeight: 600,
          fontSize: '1rem', color: 'var(--bj-espresso)', cursor: 'pointer'
        }}
      >
        {label}
        <span style={{
          fontSize: '0.75rem', opacity: 0.5,
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.25s', display: 'inline-block'
        }}>▼</span>
      </button>
      {open && (
        <div style={{ paddingBottom: 8, paddingLeft: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function MobileLink({ to, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      style={{
        display: 'block', padding: '10px 0',
        fontWeight: 500, fontSize: '0.95rem',
        color: 'var(--bj-espresso)',
        borderBottom: '1px solid var(--bj-cream-soft)'
      }}
    >
      {label}
    </Link>
  );
}

export default function Navbar() {
  const { t, i18n }       = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeLang, setActiveLang] = useState(localStorage.getItem('bj_lang') || 'en');
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(function() { setMobileOpen(false); }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(function() {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return function() { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  function triggerGoogleTranslate(lang) {
    setActiveLang(lang);
    window.switchLanguage(lang);
  }

  const activeLinkStyle = { color: 'var(--bj-clay)' };

  const langBtn = function(lang) {
    return {
      padding: '4px 10px', borderRadius: 6,
      border: '1.5px solid var(--bj-sand)',
      background: activeLang === lang ? 'var(--bj-espresso)' : 'transparent',
      color: activeLang === lang ? '#fff' : 'var(--bj-espresso)',
      cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
      transition: 'all 0.2s', fontFamily: 'var(--font-body)'
    };
  };

  return (
    <>
      <header style={{
        background: 'var(--bj-cream)',
        borderBottom: '1px solid var(--bj-sand)',
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        boxShadow: '0 2px 12px rgba(17,45,78,0.06)'
      }}>
        <div style={{
          maxWidth: 1180, margin: '0 auto', padding: '0 24px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', height: 70
        }}>

          {/* LOGO */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
            <img src="/logo.png" alt="Bluejay Travels" style={{ height: 48, width: 'auto', display: 'block' }} />
          </Link>

          {/* DESKTOP NAV */}
          <nav style={{
            gap: 28, alignItems: 'center',
            fontWeight: 600, fontSize: '0.92rem'
          }} className="desktop-only">
            <NavLink to="/" style={function(s) { return s.isActive ? activeLinkStyle : {}; }}>
              {t('nav_home')}
            </NavLink>

            <NavDropdown label={t('nav_about')}>
              <DropdownLink to="/about"      icon="🏢" label="About Us"      sub="Our story and values" />
              <Divider />
              <DropdownLink to="/leadership" icon="👥" label="Our Leadership" sub="Meet the team" />
            </NavDropdown>

            <NavDropdown label={t('nav_services')}>
              {services.map(function(s) {
                return <DropdownLink key={s.slug} to={'/services/' + s.slug} label={s.name} />;
              })}
            </NavDropdown>

            <NavLink to="/destinations" style={function(s) { return s.isActive ? activeLinkStyle : {}; }}>
              {t('nav_destinations')}
            </NavLink>

            <NavLink to="/blog" style={function(s) { return s.isActive ? activeLinkStyle : {}; }}>
              {t('nav_blog')}
            </NavLink>

            <NavLink to="/contact" style={function(s) { return s.isActive ? activeLinkStyle : {}; }}>
              {t('nav_contact')}
            </NavLink>
          </nav>

          {/* DESKTOP RIGHT */}
          <div style={{ alignItems: 'center', gap: 10 }} className="desktop-only">
            <button style={langBtn('en')} onClick={function() { triggerGoogleTranslate('en'); }}>EN</button>
            <button style={langBtn('fr')} onClick={function() { triggerGoogleTranslate('fr'); }}>FR</button>
            <Link
            to="/feedback"
            className="btn btn-primary"
            style={{ padding: '9px 18px', fontSize: '0.88rem' }}
          >
            Feedback
          </Link>
          </div>

          {/* HAMBURGER — mobile only */}
          {/* MOBILE RIGHT — lang switcher + hamburger */}
          <div className="mobile-only" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Language toggle — always visible on mobile header */}
            <div style={{
              display: 'flex',
              background: 'var(--bj-sand)',
              borderRadius: 20,
              padding: 3,
              gap: 2
            }}>
              <button
                onClick={function() { triggerGoogleTranslate('en'); }}
                style={{
                  padding: '3px 10px',
                  borderRadius: 16,
                  border: 'none',
                  background: activeLang === 'en' ? 'var(--bj-espresso)' : 'transparent',
                  color: activeLang === 'en' ? '#fff' : 'var(--bj-espresso)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  fontFamily: 'var(--font-body)',
                  transition: 'all 0.2s'
                }}
              >
                EN
              </button>
              <button
                onClick={function() { triggerGoogleTranslate('fr'); }}
                style={{
                  padding: '3px 10px',
                  borderRadius: 16,
                  border: 'none',
                  background: activeLang === 'fr' ? 'var(--bj-espresso)' : 'transparent',
                  color: activeLang === 'fr' ? '#fff' : 'var(--bj-espresso)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  fontFamily: 'var(--font-body)',
                  transition: 'all 0.2s'
                }}
              >
                FR
              </button>
            </div>

            {/* Hamburger */}
            <button
              onClick={function() { setMobileOpen(!mobileOpen); }}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{
                background: 'none', border: 'none',
                cursor: 'pointer', padding: 8,
                flexDirection: 'column',
                gap: 5, alignItems: 'center', justifyContent: 'center'
              }}
            >
              <span style={{ display: 'block', width: 24, height: 2, background: 'var(--bj-espresso)', borderRadius: 2, transform: mobileOpen ? 'rotate(45deg) translateY(7px)' : 'rotate(0)', transition: 'transform 0.25s' }} />
              <span style={{ display: 'block', width: 24, height: 2, background: 'var(--bj-espresso)', borderRadius: 2, opacity: mobileOpen ? 0 : 1, transition: 'opacity 0.25s' }} />
              <span style={{ display: 'block', width: 24, height: 2, background: 'var(--bj-espresso)', borderRadius: 2, transform: mobileOpen ? 'rotate(-45deg) translateY(-7px)' : 'rotate(0)', transition: 'transform 0.25s' }} />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 49,
          background: 'rgba(17,45,78,0.4)',
          backdropFilter: 'blur(2px)'
        }} onClick={function() { setMobileOpen(false); }} />
      )}

      <div style={{
        position: 'fixed', top: 70, left: 0, right: 0,
        background: 'var(--bj-cream)', zIndex: 49,
        padding: '16px 24px 32px',
        boxShadow: '0 12px 40px rgba(17,45,78,0.15)',
        transform: mobileOpen ? 'translateY(0)' : 'translateY(-110%)',
        transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
        maxHeight: 'calc(100vh - 70px)',
        overflowY: 'auto'
      }}>
        {/* Main links */}
        <MobileLink to="/" label={t('nav_home')} onClick={function() { setMobileOpen(false); }} />

        <MobileAccordion label={t('nav_about')}>
          <MobileLink to="/about"      label="About Us"      onClick={function() { setMobileOpen(false); }} />
          <MobileLink to="/leadership" label="Our Leadership" onClick={function() { setMobileOpen(false); }} />
        </MobileAccordion>

        <MobileAccordion label={t('nav_services')}>
          {services.map(function(s) {
            return <MobileLink key={s.slug} to={'/services/' + s.slug} label={s.name} onClick={function() { setMobileOpen(false); }} />;
          })}
        </MobileAccordion>

        <MobileLink to="/destinations" label={t('nav_destinations')} onClick={function() { setMobileOpen(false); }} />
        <MobileLink to="/blog"         label={t('nav_blog')}         onClick={function() { setMobileOpen(false); }} />
        <MobileLink to="/contact"      label={t('nav_contact')}      onClick={function() { setMobileOpen(false); }} />

        {/* Mobile CTA + lang */}
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Link
            to="/feedback"
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={function() { setMobileOpen(false); }}
          >
            Feedback
          </Link>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...langBtn('en'), flex: 1 }} onClick={function() { triggerGoogleTranslate('en'); }}>English</button>
            <button style={{ ...langBtn('fr'), flex: 1 }} onClick={function() { triggerGoogleTranslate('fr'); }}>Français</button>
          </div>
        </div>
      </div>
    </>
  );
}